import React, { useEffect, useState } from 'react';
import { IImageOverlayModel } from '../../../models/ImageOverlayModel';
import { AssetImageOverlay } from '../../image-overlay/AssetImageOverlay';
import PropertyValueList from '../../property-value-list/PropertyValueList';
import { DASHBOARDS, R, WIDGETS } from '../../../rbac';
import { ProtectComponent } from '../../../rbac';

interface ImageDiagramProps {
  backgroundImage: string;
  imageOverlays: IImageOverlayModel[];
  exceptionProperties: any[];
  alarmProperties: any[];
  wellTestProperties: any[];
  statusRegisterProperties: any[];
  wellStatusOverview: any[];
  diagramType: number;
  onRefresh: () => void;
}

const HEADER_HEIGHT = 50;

const ImageDiagram = ({
  backgroundImage,
  imageOverlays,
  exceptionProperties,
  alarmProperties,
  wellTestProperties,
  statusRegisterProperties,
  wellStatusOverview,
  diagramType,
  onRefresh,
}: ImageDiagramProps) => {
  const [statusRegistersHeight, setStatusRegistersHeight] = useState<number>(0);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const resizeObserver = new ResizeObserver((entries) => {
    window.requestAnimationFrame(() => {
      if (!Array.isArray(entries) || !entries.length) {
        return;
      }

      setStatusRegistersHeight(Math.floor(entries[0].contentRect.height));
    });
  });

  useEffect(() => {
    if (!containerRef.current) return;

    resizeObserver.observe(containerRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef]);

  return (
    <div className='xl:flex flex-row gap-3 w-full'>
      <div className='xl:flex flex-column flex-1'>
        <ProtectComponent
          resource={R.widget(DASHBOARDS.ASSET_STATUS, WIDGETS.ASSET_SCHEMATICS)}
          action='view'
          widgetMessageName='Asset Schematics'
          assetDashboard={true}
        >
          <AssetImageOverlay
            bkgndImgUrl={backgroundImage}
            data={imageOverlays}
            diagramType={diagramType}
            containerRef={containerRef}
            onRefresh={onRefresh}
          />
        </ProtectComponent>
      </div>
      <div className='lg:flex flex-row flex-1 gap-3'>
        <div className='pt-2 xl:pt-0 flex flex-column flex-1'>
          <ProtectComponent
            resource={R.widget(DASHBOARDS.ASSET_STATUS, WIDGETS.ASSET_STATUS_REGISTERS)}
            action='view'
            widgetMessageName='Status registers'
            assetDashboard={true}
          >
            <PropertyValueList
              description='Status registers'
              properties={statusRegisterProperties}
              showLength={false}
              maxHeight={`${statusRegistersHeight - HEADER_HEIGHT}px`}
            />
          </ProtectComponent>
        </div>
        <div className='pt-2 xl:pt-0 flex flex-column flex-1 gap-3'>
          <ProtectComponent
            resource={R.widget(DASHBOARDS.ASSET_STATUS, WIDGETS.ASSET_COMMS)}
            action='view'
            assetDashboard={true}
            widgetMessageName='Communications'
          >
            <PropertyValueList
              description={'Communications'}
              properties={wellStatusOverview}
              showLength={false}
            />
          </ProtectComponent>
          <ProtectComponent
            resource={R.widget(DASHBOARDS.ASSET_STATUS, WIDGETS.ASSET_EXCEPTIONS)}
            action='view'
            widgetMessageName='Exceptions'
            assetDashboard={true}
          >
            <PropertyValueList
              description='Exceptions'
              properties={exceptionProperties}
              showLength={false}
              noDescriptionOnProperties={true}
            />
          </ProtectComponent>
          <ProtectComponent
            resource={R.widget(DASHBOARDS.ASSET_STATUS, WIDGETS.ASSET_ALARMS)}
            action='view'
            widgetMessageName='Alarms'
            assetDashboard={true}
          >
            <PropertyValueList
              description='Alarms'
              properties={alarmProperties}
              showLength={true}
              maxHeight='191px'
            />
          </ProtectComponent>
          <ProtectComponent
            resource={R.widget(DASHBOARDS.ASSET_STATUS, WIDGETS.ASSET_LAST_WELL_TEST)}
            action='view'
            widgetMessageName='Last well test'
            assetDashboard={true}
          >
            <PropertyValueList
              description='Last well test'
              properties={wellTestProperties}
              showLength={false}
            />
          </ProtectComponent>
        </div>
      </div>
    </div>
  );
};

export default ImageDiagram;
